import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import CalendarPicker from './CalendarPicker';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';

const EVENT_TYPES = [
  'ITP — Technical Inspection',
  'RCA — Insurance Renewal',
  'CASCO — Insurance Renewal',
  'Rovinieta — Road Fee',
  'Oil Change / Service',
  'Tire Service',
  'Electrical / Diagnosis',
  'Body / Repair',
  'Accident Repair',
  'Administrative',
  'Other',
];

const PRIORITIES = [
  { value: 'Normal',   color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { value: 'Expedite', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { value: 'CRITICAL', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
];

const EventForm = ({ isOpen, onClose, event, onSave, supplierName }) => {
  const toDateInputValue = (value) => {
    if (!value) return '';
    const s = String(value);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm}-${dd}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return '';
  };

  const today = new Date().toISOString().slice(0, 10);

  const EMPTY_FORM = {
    plate: '', car: '', eventType: '', customEventType: '', evt: '',
    resp: '', created: today, estEnd: '',
    notes: '', priority: 'Normal', amount: '', currency: 'RON',
  };

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      const knownType = EVENT_TYPES.includes(event.eventType || '');
      setFormData({
        plate:           event.plate     || '',
        car:             event.car       || '',
        eventType:       knownType ? (event.eventType || '') : (event.eventType ? 'Other' : ''),
        customEventType: knownType ? '' : (event.eventType || ''),
        evt:             event.evt       || '',
        resp:            event.resp      || '',
        created:         toDateInputValue(event.created) || today,
        estEnd:          toDateInputValue(event.estEnd),
        notes:           event.notes    || '',
        priority:        event.priority || 'Normal',
        amount:          event.amount != null ? String(event.amount) : '',
        currency:        event.currency || 'RON',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [event, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...(event && event.id ? { id: event.id } : {}),
        plate:        supplierName ? '' : formData.plate,
        car:          supplierName ? '' : formData.car,
        supplierName: supplierName || '',
        eventType: formData.eventType === 'Other' && formData.customEventType.trim()
          ? formData.customEventType.trim()
          : formData.eventType,
        evt:       formData.evt,
        resp:      formData.resp,
        status:    event?.status || 'Activ',
        created:   formData.created ? new Date(formData.created).toISOString() : new Date().toISOString(),
        estEnd:    formData.estEnd  ? new Date(formData.estEnd).toISOString()  : '',
        notes:     formData.notes,
        priority:  formData.priority,
        amount:    formData.amount !== '' ? Number(formData.amount) : null,
        currency:  formData.currency,
      };
      await onSave(payload);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!(event && (event._id || event.id));
  const fd = useFormData();

  const handlePlateSelect = (plate) => {
    const v = fd.getVehicleByPlate(plate);
    if (v) setFormData(prev => ({
      ...prev, plate,
      car: [v.brand, v.model].filter(Boolean).join(' ') || prev.car,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Event' : 'New Event'} size="sm">
      <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: -8, marginBottom: 16 }}>
        {supplierName ? 'Add event' : 'Add vehicle event'}
      </p>
      <form onSubmit={handleSubmit}>

        {supplierName ? (
          <div className="form-group">
            <label className="form-label">Supplier</label>
            <div style={{
              padding: '8px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-1)',
            }}>
              {supplierName}
            </div>
          </div>
        ) : (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Registration number</label>
              <AutocompleteInput
                name="plate" value={formData.plate} onChange={handleChange}
                suggestions={fd.plates} onSelect={handlePlateSelect}
                required placeholder="e.g. B-12-XYZ"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Brand / Model</label>
              <AutocompleteInput
                name="car" value={formData.car} onChange={handleChange}
                suggestions={fd.vehicleNames}
                required placeholder="e.g. Dacia Logan"
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Event type *</label>
          <select name="eventType" className="form-input" value={formData.eventType} onChange={handleChange} required>
            <option value="">— Select type —</option>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {formData.eventType === 'Other' && (
          <div className="form-group">
            <label className="form-label">Specify event type *</label>
            <input
              type="text" name="customEventType" className="form-input"
              value={formData.customEventType} onChange={handleChange}
              required autoFocus placeholder="e.g. Exhaust Repair, Windshield, Towing…"
            />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Event name *</label>
          <AutocompleteInput
            name="evt" value={formData.evt} onChange={handleChange}
            suggestions={fd.eventNames}
            required placeholder="e.g. 10W40 oil change + air/oil/AC filters"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Creation date</label>
            <CalendarPicker name="created" value={formData.created} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label className="form-label">Estimated completion date</label>
            <CalendarPicker name="estEnd" value={formData.estEnd} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Responsible</label>
          <AutocompleteInput
            name="resp" value={formData.resp} onChange={handleChange}
            suggestions={fd.driverNames}
            required placeholder="e.g. Alex Donovan"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Note / Description</label>
          <textarea
            name="notes" className="form-input"
            value={formData.notes} onChange={handleChange}
            rows={3} style={{ resize: 'vertical' }}
            placeholder="Additional details about the event, service used, parts needed..."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Amount spent</label>
            <input
              type="number" name="amount" className="form-input" min="0" step="0.01"
              value={formData.amount} onChange={handleChange}
              placeholder="e.g. 350"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Currency</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['RON', 'EUR'].map((cur) => (
                <label
                  key={cur}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${formData.currency === cur ? '#0077b6' : 'var(--border)'}`,
                    background: formData.currency === cur ? '#eff6ff' : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio" name="currency" value={cur}
                    checked={formData.currency === cur}
                    onChange={handleChange}
                    style={{ accentColor: '#0077b6', margin: 0 }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13, color: formData.currency === cur ? '#0077b6' : 'var(--text-2)' }}>
                    {cur}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Priority</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PRIORITIES.map(({ value, color, bg, border }) => (
              <label
                key={value}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `1.5px solid ${formData.priority === value ? border : 'var(--border)'}`,
                  background: formData.priority === value ? bg : 'transparent',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio" name="priority" value={value}
                  checked={formData.priority === value}
                  onChange={handleChange}
                  style={{ accentColor: color, margin: 0 }}
                />
                <span style={{ fontWeight: 600, fontSize: 13, color: formData.priority === value ? color : 'var(--text-2)' }}>
                  {value}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : isEditing ? 'Update Event' : '+ Create Event'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventForm;
